import type { Operator, PublicQuestion, Question } from './types';
import type { AdditionSpec, MultiplicationSpec, YearConfig } from './year-config';

export function buildAdditionPool(): Question[] {
  return buildAdditionPoolFromSpec({});
}

export function buildMultiplicationPool(): Question[] {
  const questions: Question[] = [];
  for (let operand1 = 1; operand1 <= 12; operand1 += 1) {
    for (let operand2 = 1; operand2 <= 12; operand2 += 1) {
      questions.push(toQuestion(0, operand1, 'x', operand2));
    }
  }
  return questions;
}

export function toPublicQuestion(question: Question): PublicQuestion {
  const { correctAnswer: _correctAnswer, ...publicQuestion } = question;
  return publicQuestion;
}

export function questionText(question: Pick<Question, 'operand1' | 'operator' | 'operand2'>): string {
  return `${question.operand1} ${question.operator} ${question.operand2}`;
}

export function questionKey(question: Pick<Question, 'operand1' | 'operator' | 'operand2'>): string {
  return `${question.operand1}${question.operator}${question.operand2}`;
}

export function toQuestion(
  position: number,
  operand1: number,
  operator: Operator,
  operand2: number
): Question {
  return {
    position,
    operand1,
    operator,
    operand2,
    correctAnswer: operator === '+' ? operand1 + operand2 : operand1 * operand2
  };
}

export function shuffle<T>(items: T[], random: () => number = Math.random): T[] {
  const shuffled = items.slice();
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
  }
  return shuffled;
}

function factsToQuestions(
  facts: ReadonlyArray<readonly [number, number]>,
  operator: Operator
): Question[] {
  return facts.map(([operand1, operand2]) => toQuestion(0, operand1, operator, operand2));
}

export function buildAdditionPoolFromSpec(spec: AdditionSpec): Question[] {
  if (spec.facts) {
    return factsToQuestions(spec.facts, '+');
  }
  const minOperand = spec.minOperand ?? 1;
  const maxOperand = spec.maxOperand ?? 9;
  const questions: Question[] = [];
  for (let operand1 = minOperand; operand1 <= maxOperand; operand1 += 1) {
    for (let operand2 = minOperand; operand2 <= maxOperand; operand2 += 1) {
      questions.push(toQuestion(0, operand1, '+', operand2));
    }
  }
  return questions;
}

export function buildMultiplicationPoolFromSpec(spec: MultiplicationSpec): Question[] {
  if (spec.facts) {
    return factsToQuestions(spec.facts, 'x');
  }
  const minMultiplier = spec.minMultiplier ?? 2;
  const maxMultiplier = spec.maxMultiplier ?? 12;
  const questions: Question[] = [];
  for (const table of spec.tables ?? []) {
    for (let multiplier = minMultiplier; multiplier <= maxMultiplier; multiplier += 1) {
      questions.push(toQuestion(0, multiplier, 'x', table));
    }
  }
  return questions;
}

export interface GenerateYearTestOptions {
  /** Optional slider override (0..1 share of additions); ignored for single-type years. */
  additionRatio?: number;
  random?: () => number;
}

export function generateYearTest(config: YearConfig, options: GenerateYearTestOptions = {}): Question[] {
  const random = options.random ?? Math.random;
  const total = config.questionCount;
  const additionPool = config.addition ? shuffle(buildAdditionPoolFromSpec(config.addition), random) : [];
  const multiplicationPool = config.multiplication
    ? shuffle(buildMultiplicationPoolFromSpec(config.multiplication), random)
    : [];

  const additionCount = resolveAdditionCount(config, additionPool.length, multiplicationPool.length, options.additionRatio);
  const multiplicationCount = total - additionCount;

  const selected: Question[] = [
    ...additionPool.slice(0, additionCount),
    ...multiplicationPool.slice(0, multiplicationCount)
  ];

  if (selected.length < total) {
    // Safety net: only reachable if a config's own pools are smaller than its question count.
    const seen = new Set(selected.map(questionKey));
    const backfill = [...additionPool, ...multiplicationPool].filter(
      (question) => !seen.has(questionKey(question))
    );
    selected.push(...backfill.slice(0, total - selected.length));
  }

  return shuffle(selected, random)
    .slice(0, total)
    .map((question, index) => ({ ...question, position: index + 1 }));
}

/**
 * The addition count a year can actually produce, bounded by how many unique facts exist.
 * The slider may only move within [min, max]; beyond that there are not enough unique facts to
 * fill the test, so the mix is clamped rather than silently padded with repeats or the other type.
 */
export function additionCountBounds(
  config: YearConfig,
  additionPoolSize?: number,
  multiplicationPoolSize?: number
): { min: number; max: number } {
  const additionSize =
    additionPoolSize ?? (config.addition ? buildAdditionPoolFromSpec(config.addition).length : 0);
  const multiplicationSize =
    multiplicationPoolSize ??
    (config.multiplication ? buildMultiplicationPoolFromSpec(config.multiplication).length : 0);
  return {
    min: Math.max(0, config.questionCount - multiplicationSize),
    max: Math.min(config.questionCount, additionSize)
  };
}

function resolveAdditionCount(
  config: YearConfig,
  additionPoolSize: number,
  multiplicationPoolSize: number,
  additionRatio?: number
): number {
  const { min, max } = additionCountBounds(config, additionPoolSize, multiplicationPoolSize);
  const requested =
    additionRatio == null || !config.mixAdjustable
      ? config.additionCount
      : Math.round(Math.max(0, Math.min(1, additionRatio)) * config.questionCount);
  return Math.max(min, Math.min(max, requested));
}
